/**
 * What to tell someone whose sign-in pop-up never opened.
 *
 * Every provider here signs in through a pop-up on desktop, and every one of
 * them can be blocked. They used to answer that with four different strings -
 * or, in Google's case, with "cancelled or blocked", which merges the one
 * thing the user did on purpose with the one thing they need to fix.
 *
 * Two facts change the advice, and neither is visible from the string alone:
 *
 * 1. **Where the browser's notice is.** In a tab it is an icon at the right of
 *    the address bar, which people find. An installed app window has no
 *    address bar, so Edge and Chrome tuck the same icon into a title bar a few
 *    pixels high - present, but nobody looks there. This is not a PWA bug and
 *    not something the app can turn off; it is the ordinary pop-up blocker
 *    with its notice moved somewhere unhelpful.
 *
 * 2. **Whether a click asked for the pop-up.** A window opened outside a user
 *    gesture is blocked by default whatever the site's pop-up permission says,
 *    and this app does ask outside one: the player meets an unplayable Drive
 *    track while auto-advancing and tries to authorise from an effect.
 *    Pressing the button by hand fixes that case on its own, without touching
 *    any browser setting - so say so, rather than sending someone into
 *    settings that were never the problem.
 */

/** True in an installed window, where there is no address bar to look at. */
export function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: window-controls-overlay)').matches === true ||
    // iOS Safari's own flag, which predates display-mode and still answers
    // where the media query does not.
    (window.navigator as any).standalone === true
  );
}

/**
 * Whether a click is still vouching for what we are about to do.
 *
 * Answers true where the API is missing - Safari has no
 * navigator.userActivation - because "we cannot tell" must not print an
 * accusation at a user who had just pressed the button.
 */
export function hasUserActivation() {
  const activation = (navigator as any).userActivation;

  return activation ? activation.isActive === true : true;
}

/**
 * Where the browser put the notice nobody saw.
 *
 * Split out from the message below because the silent-renewal warning needs
 * this sentence and none of the rest of it.
 */
export function popupNoticeLocation() {
  return isStandalone()
    ? 'This window has no address bar, so the blocked pop-up notice is easy to miss: ' +
        'it is a small icon in the title bar, beside the menu button.'
    : 'The blocked pop-up notice is the small icon at the right of the address bar.';
}

/**
 * @param provider Name as the user knows it - "Google", "Dropbox".
 * @param hadUserGesture Pass the answer from the moment the pop-up was asked
 * for. The default reads it now, which is right when the failure is reported
 * synchronously and only approximate when it is not.
 */
export function popupBlockedMessage(provider: string, hadUserGesture = hasUserActivation()) {
  const why = hadUserGesture
    ? ''
    : ' Nothing you pressed asked for it - the app requested it while carrying on by ' +
      'itself, and browsers block those - so pressing the sign-in button yourself may ' +
      'be all it takes.';

  return (
    `${provider} sign-in needs a pop-up window, and this browser blocked it.` +
    `${why} Otherwise allow pop-ups for this site. ${popupNoticeLocation()}`
  );
}
